import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { CollectionTypeChip } from "@/Collections/Collection/CollectionTypeChip";
import { TagList } from "@/shared/components/TagList";
import { UserLink } from "@/shared/components/UserLink";
import {
  cellStyles,
  createColumnHelper,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { RobotoPrincipal, RobotoPrincipalType } from "@/shared/principal";

import { CollectionRecord } from "./CollectionRecord";
import styles from "./CollectionsTableColumns.module.css";

const columnHelper = createColumnHelper<CollectionRecord>();

function formatTimestamp(value: string | undefined | null): string {
  if (!value) {
    return "";
  }
  const d = dayjs(value);
  return dayjs().diff(d, "day") >= 7 ? d.format("MMM D, YYYY") : d.fromNow();
}

function principalCellRenderer(
  p: ICellRendererParams<CollectionRecord, string>,
) {
  const value = p.value ?? "";
  const principal = RobotoPrincipal.fromString(value);
  if (principal?.ptype === RobotoPrincipalType.User) {
    return (
      <UserLink
        userId={principal.unqualifiedId}
        linkSx={{ fontSize: "var(--font-size-xs)" }}
      />
    );
  }
  return <span>{principal?.unqualifiedId ?? value}</span>;
}

export const collectionsTableColumns = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    sortable: true,
    cellRenderer: (p: ICellRendererParams<CollectionRecord, string | null>) => {
      const record = p.data;
      if (!record) {
        return null;
      }
      const label = record.name ?? record.collection_id;
      return (
        <Link
          to={`/collections/${record.collection_id}`}
          className={styles.nameLink}
        >
          {label}
        </Link>
      );
    },
  }),
  columnHelper.accessor("collection_id", {
    headerName: "ID",
    width: 220,
    cellClass: cellStyles.monoCell,
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    width: 150,
    sortable: true,
    valueFormatter: (p) => formatTimestamp(p.value),
  }),
  columnHelper.accessor("updated_by", {
    headerName: "Updated By",
    width: 150,
    sortable: true,
    cellRenderer: principalCellRenderer,
  }),
  columnHelper.accessor("updated", {
    headerName: "Updated",
    width: 150,
    sortable: true,
    valueFormatter: (p) => formatTimestamp(p.value),
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
  }),
  columnHelper.accessor("resource_type", {
    headerName: "Entity Type",
    width: 140,
    sortable: true,
    valueFormatter: (p) => {
      const v = p.value;
      if (!v) {
        return "";
      }
      return v.charAt(0).toUpperCase() + v.slice(1) + "s";
    },
    cellRenderer: (
      p: ICellRendererParams<
        CollectionRecord,
        CollectionRecord["resource_type"]
      >,
    ) => {
      if (!p.value) {
        return null;
      }
      return <CollectionTypeChip resourceType={p.value} />;
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 2,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: (p: ICellRendererParams<CollectionRecord, string[]>) => (
      <TagList tags={p.value ?? []} maxDisplayCount={3} />
    ),
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created By",
    width: 150,
    sortable: true,
    cellRenderer: principalCellRenderer,
  }),
];
